import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Modal } from '../components/Modal';
import { PasswordPromptModal } from '../components/PasswordPromptModal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { useSession } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { useCurrentSite } from '../lib/currentSite';
import {
  listSites,
  createSite,
  archiveSite,
  deleteSite,
  updateSite,
  type Site,
} from '../lib/db';
import { cacheListByOrg } from '../lib/cache';
import { lookupPostcode, isValidPostcodeFormat } from '../lib/postcode';
import { captureGeolocation } from '../lib/geo';
import { LoadingIndicator } from '../components/LoadingIndicator';

// Shared error stringifier — Supabase throws plain objects with .message,
// not real Error instances, so a naive String(e) prints "[object Object]".
//
// The final fallback is only hit when we genuinely can't extract anything
// useful (no Error, no .message, not a string). In that case the user is
// stuck — give them somewhere to go rather than a dead-end "try again".
function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  if (typeof e === 'string') return e;
  return 'Something went wrong. Please try again — and if it keeps happening, email us at hello@safeandsorted.com and we\'ll dig in.';
}

interface Draft {
  name: string;
  postcode: string;
  address: string;
  radius_m: number;
  project_type: 'quick_job' | 'small_refurb' | 'full_site' | null;
  in_charge: boolean | null;
}

const EMPTY: Draft = { name: '', postcode: '', address: '', radius_m: 250, project_type: null, in_charge: null };

const RADIUS_OPTIONS: { value: number; label: string }[] = [
  { value: 100,  label: '100m — tight (small yard / office)' },
  { value: 250,  label: '250m — normal site (recommended)' },
  { value: 500,  label: '500m — large site / multi-plot' },
  { value: 1000, label: '1km — sprawling development' },
];

interface PostcodeStatus {
  ok: boolean;
  label: string;
  lat?: number;
  lng?: number;
}

export function Sites() {
  const { org, loading: orgLoading, error: orgError } = useUserOrg();
  const { session } = useSession();
  const { setCurrentSite } = useCurrentSite();
  const { confirm, alert } = useConfirm();
  const nav = useNavigate();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [editing, setEditing] = useState<Site | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [pcStatus, setPcStatus] = useState<PostcodeStatus | null>(null);
  const [checkingPostcode, setCheckingPostcode] = useState(false);
  const [usingLocation, setUsingLocation] = useState(false);
  // Password-confirm-delete flow state
  const [pwdTarget, setPwdTarget] = useState<Site | null>(null);
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdBusy, setPwdBusy] = useState(false);

  // Initial load — stale-while-revalidate. Renders cached sites
  // instantly on mount, then refreshes from the network in the
  // background. Post-mutation refreshes (after save/archive/delete)
  // still call refresh() directly for network-only behaviour.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    let haveCache = false;
    cacheListByOrg<Site>('sites', org.org_id).then((cached) => {
      if (cancelled) return;
      if (cached.length > 0) {
        haveCache = true;
        setSites(cached);
        setLoading(false);
      }
    }).catch(() => { /* cache miss is fine */ });
    listSites(org.org_id).then((list) => {
      if (cancelled) return;
      setSites(list);
      setError(null);
    }).catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled && !haveCache) setLoading(false); });
    return () => { cancelled = true; };
  }, [org]);

  useEffect(() => {
    if (searchParams.get('add') === '1') {
      startAdd();
      // Clear the param so navigating away & back doesn't auto-reopen
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  async function refresh(orgId: string) {
    setLoading(true);
    try {
      setSites(await listSites(orgId));
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }

  function startAdd() {
    setEditing(null);
    setDraft(EMPTY);
    setPcStatus(null);
    setError(null);
    setShowModal(true);
  }

  function startEdit(s: Site) {
    setEditing(s);
    setDraft({
      name: s.name,
      postcode: '',
      address: s.address ?? '',
      radius_m: s.geofence_radius_m ?? 250,
      project_type: s.project_type ?? null,
      in_charge: s.in_charge ?? null,
    });
    setPcStatus(
      s.centre_lat != null && s.centre_lng != null
        ? { ok: true, label: 'Geofence already set' }
        : null
    );
    setError(null);
    setShowModal(true);
  }

  async function useCurrentLocation() {
    setUsingLocation(true);
    setError(null);
    try {
      const geo = await captureGeolocation(8000);
      if (!geo) {
        setPcStatus({
          ok: false,
          label: 'Could not get your location. Allow location access in your browser and try again.',
        });
        return;
      }
      setPcStatus({
        ok: true,
        label: `✓ Centre set to your current location (±${Math.round(geo.accuracy_m)}m)`,
        lat: geo.lat,
        lng: geo.lng,
      });
    } finally {
      setUsingLocation(false);
    }
  }

  async function checkPostcode() {
    if (!draft.postcode.trim()) {
      setPcStatus(null);
      return;
    }
    if (!isValidPostcodeFormat(draft.postcode)) {
      setPcStatus({ ok: false, label: 'Doesn\'t look like a UK postcode' });
      return;
    }
    setCheckingPostcode(true);
    try {
      const r = await lookupPostcode(draft.postcode);
      if (!r) {
        setPcStatus({ ok: false, label: 'Postcode not recognised' });
      } else {
        const where = r.admin_district ? `near ${r.admin_district}` : '';
        setPcStatus({
          ok: true,
          label: `✓ Geofence will be set ${where}`.trim(),
          lat: r.latitude,
          lng: r.longitude,
        });
      }
    } catch (e) {
      setPcStatus({ ok: false, label: errMsg(e) });
    } finally {
      setCheckingPostcode(false);
    }
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!org) return;
    if (!draft.name.trim()) { setError('Name is required.'); return; }
    if (draft.in_charge === null) {
      setError('Tell us if you\'re running this site or working under someone else.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Resolve postcode → coords if filled (and not already validated)
      let centre: { lat: number; lng: number } | null =
        pcStatus?.ok && pcStatus.lat != null && pcStatus.lng != null
          ? { lat: pcStatus.lat, lng: pcStatus.lng }
          : null;
      if (draft.postcode.trim() && !centre) {
        try {
          const r = await lookupPostcode(draft.postcode);
          if (r) centre = { lat: r.latitude, lng: r.longitude };
        } catch {
          /* ignore — save without coords */
        }
      }

      if (editing) {
        const patch: Partial<Site> = {
          name: draft.name.trim(),
          address: draft.address.trim() || null,
          geofence_radius_m: draft.radius_m,
          project_type: draft.project_type,
          in_charge: draft.in_charge,
        };
        if (centre) {
          patch.centre_lat = centre.lat;
          patch.centre_lng = centre.lng;
        }
        await updateSite(editing.id, patch);
      } else {
        const created = await createSite({
          orgId: org.org_id,
          name: draft.name.trim(),
          address: draft.address.trim() || null,
          projectType: draft.project_type,
          // Always use the user's site-level answer — builders vary site
          // by site (PC on one, sub on another), so the org-level default
          // isn't reliable. We validate above that in_charge is set.
          inCharge: draft.in_charge,
        });
        const patch: Partial<Site> = { geofence_radius_m: draft.radius_m };
        if (centre) {
          patch.centre_lat = centre.lat;
          patch.centre_lng = centre.lng;
        }
        await updateSite(created.id, patch);

        // After creating a site, drop straight onto its Setup Checklist —
        // that page lists every doc / form / RAMS / permit this job needs
        // based on project type, role, and risky activities. Items tick
        // off as they're filled in.
        setShowModal(false);
        setEditing(null);
        setDraft(EMPTY);
        setPcStatus(null);
        await refresh(org.org_id);
        setCurrentSite(created.id);
        nav(`/sites/${created.id}/setup`);
        return;
      }
      setShowModal(false);
      setEditing(null);
      setDraft(EMPTY);
      setPcStatus(null);
      await refresh(org.org_id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function onArchive(s: Site) {
    if (!org) return;
    if (!(await confirm({
      title: `Archive "${s.name}"?`,
      body: 'It\'ll move to Completed jobs. You can restore it later.',
      confirmLabel: 'Archive',
    }))) return;
    setError(null);
    try {
      await archiveSite(s.id);
      setShowModal(false);
      setEditing(null);
      await refresh(org.org_id);
    } catch (e) {
      setError(errMsg(e));
    }
  }

  async function onDelete(s: Site) {
    if (!org) return;
    if (!session?.user?.email) {
      await alert({
        title: "Couldn't read your account",
        body: 'Please sign out and back in, then try again.',
      });
      return;
    }
    // First confirm intent
    const ok = await confirm({
      title: `Permanently delete "${s.name}"?`,
      body: 'This deletes the site AND every sign-in, RAMS, form, permit and document attached to it. It cannot be undone.',
      confirmLabel: 'Continue',
      danger: true,
    });
    if (!ok) return;
    // Open password modal — actual delete happens in onPasswordConfirm.
    setPwdError(null);
    setPwdBusy(false);
    setPwdTarget(s);
  }

  /** Called by PasswordPromptModal when the user submits a password. */
  async function onPasswordConfirm(password: string) {
    if (!org || !pwdTarget || !session?.user?.email) return;
    setPwdBusy(true);
    setPwdError(null);
    try {
      // Verify the password by attempting a re-sign-in with the existing session.
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: session.user.email,
        password,
      });
      if (authError) {
        setPwdError("Password didn't match. Nothing was deleted.");
        setPwdBusy(false);
        return;
      }
      await deleteSite(pwdTarget.id);
      const orgId = org.org_id;
      setPwdTarget(null);
      setPwdBusy(false);
      setShowModal(false);
      setEditing(null);
      await refresh(orgId);
    } catch (e) {
      setPwdError(errMsg(e));
      setPwdBusy(false);
    }
  }

  if (orgLoading) {
    return <AppShell title="Sites" back="/"><LoadingIndicator /></AppShell>;
  }

  if (orgError || !org) {
    return (
      <AppShell title="Sites" back="/">
        <div className="banner banner-error">
          {orgError ?? "We couldn't find your organisation. Try signing out and back in."}
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Sites" back="/">
      {error && <div className="banner banner-error">{error}</div>}

      <button type="button" className="btn btn-primary" onClick={startAdd} style={{ marginBottom: 12 }}>
        + Add site
      </button>

      {loading ? (
        <LoadingIndicator label="Loading sites…" />
      ) : sites.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No sites yet. Add your first one above.
          </p>
        </div>
      ) : (
        <ul className="site-list">
          {sites.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className="site-item-link"
                style={{ width: '100%', textAlign: 'left', font: 'inherit' }}
                onClick={() => {
                  // Make this site active, then drop the user on Home (the
                  // working dashboard for the chosen site). SPA navigation
                  // — no full reload, no splash replay.
                  setCurrentSite(s.id);
                  nav('/');
                }}
              >
                <div>
                  <p className="site-name">{s.name}</p>
                  <p className="site-meta">
                    {s.role}
                    {s.address ? ` · ${s.address}` : ''}
                    {s.centre_lat != null ? ' · 📍 geofence set' : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn-tiny"
                    onClick={(e) => { e.stopPropagation(); startEdit(s); }}
                  >Edit</button>
                  <button
                    type="button"
                    className="btn-tiny btn-tiny-danger"
                    onClick={(e) => { e.stopPropagation(); onArchive(s); }}
                    aria-label={`Archive ${s.name}`}
                  >Archive</button>
                  <button
                    type="button"
                    className="btn-tiny btn-tiny-delete"
                    onClick={(e) => { e.stopPropagation(); onDelete(s); }}
                    aria-label={`Delete ${s.name}`}
                  >Delete</button>
                  <span className="chev">›</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={showModal}
        title={editing ? 'Edit site' : 'Add a site'}
        onClose={() => { setShowModal(false); setEditing(null); }}
      >
        <form onSubmit={onSave}>
          <div className="field">
            <label htmlFor="site-name">Site name *</label>
            <input
              id="site-name"
              type="text"
              required
              autoFocus
              placeholder="e.g. Bigby Lane Plot 1-4" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>

          <div className="field">
            <label>What kind of job is this?</label>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>Tells us what daily checks you'll need.</p>
            <div className="role-cards">
              <button type="button"
                className={`role-card${draft.project_type === 'quick_job' ? ' selected' : ''}`}
                onClick={() => setDraft({ ...draft, project_type: 'quick_job' })}>
                <strong>Quick job</strong>
                <span>1-2 days. In and out. No scaffold or excavation.</span>
              </button>
              <button type="button"
                className={`role-card${draft.project_type === 'small_refurb' ? ' selected' : ''}`}
                onClick={() => setDraft({ ...draft, project_type: 'small_refurb' })}>
                <strong>Small job</strong>
                <span>A few days to a few weeks. No scaffold or major excavation.</span>
              </button>
              <button type="button"
                className={`role-card${draft.project_type === 'full_site' ? ' selected' : ''}`}
                onClick={() => setDraft({ ...draft, project_type: 'full_site' })}>
                <strong>Full site</strong>
                <span>Extension, new build, multi-trade. Scaffold, excavation, the lot.</span>
              </button>
            </div>
          </div>

          {/*
            Per-site role question. Used to be hidden unless the org's
            default role was "both" — but builders genuinely vary site
            by site (PC on one job, sub on another), so we now ask
            every time. Validated on save.
          */}
          <div className="field needs-input">
            <label>Are you in charge of this site? *</label>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
              Sets what shows up on the site setup checklist — whether you
              need a full CPP and traffic plan, or just your own RAMS.
            </p>
            <div className="role-cards">
              <button type="button"
                className={`role-card${draft.in_charge === true ? ' selected' : ''}`}
                onClick={() => setDraft({ ...draft, in_charge: true })}>
                <strong>Yes — I'm running it</strong>
                <span>You're the main builder. Other trades work under you.</span>
              </button>
              <button type="button"
                className={`role-card${draft.in_charge === false ? ' selected' : ''}`}
                onClick={() => setDraft({ ...draft, in_charge: false })}>
                <strong>No — I'm a sub on this one</strong>
                <span>Working under someone else's site rules. You issue your own RAMS up to them.</span>
              </button>
            </div>
          </div>

          <div className="field">
            <label htmlFor="site-postcode">Postcode (sets the geofence centre)</label>
            <div className="postcode-row">
              <input
                id="site-postcode"
                type="text"
                placeholder="e.g. HG2 7XS"
                autoCapitalize="characters"
                value={draft.postcode}
                onChange={(e) => {
                  setDraft({ ...draft, postcode: e.target.value.toUpperCase() });
                  setPcStatus(null);
                }}
                onBlur={() => { if (draft.postcode.trim()) checkPostcode(); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); checkPostcode(); } }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={checkPostcode}
                disabled={checkingPostcode || !draft.postcode.trim()}
              >
                {checkingPostcode ? 'Checking…' : 'Check'}
              </button>
            </div>
            {pcStatus && (
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 13,
                  color: pcStatus.ok ? 'var(--success)' : 'var(--danger)',
                }}
              >
                {pcStatus.label}
              </p>
            )}
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
              Postcode gives the area centre. For a precise centre, tap the button below
              while you're physically on site — uses your phone's GPS.
            </p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={useCurrentLocation}
              disabled={usingLocation}
              style={{ marginTop: 8, width: 'auto' }}
            >
              {usingLocation ? 'Locating…' : '📍 Use my current location as centre'}
            </button>
          </div>

          <div className="field">
            <label htmlFor="site-radius">Geofence radius</label>
            <select
              id="site-radius"
              value={draft.radius_m}
              onChange={(e) => setDraft({ ...draft, radius_m: Number(e.target.value) })}
            >
              {RADIUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--ink-soft)' }}>
              Workers signing in further than this from the centre get a warning.
            </p>
          </div>

          <div className="field">
            <label htmlFor="site-addr">Address (optional)</label>
            <textarea
              id="site-addr"
              rows={2}
              value={draft.address}
              onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            />
          </div>

          <div className="btn-stack">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || !draft.name.trim()}
            >
              {submitting ? 'Saving…' : editing ? 'Save changes' : 'Save site'}
            </button>
            {editing && (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }}
                  onClick={() => onArchive(editing)}
                >
                  Archive site
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ color: '#FFF', background: 'var(--danger)', borderColor: 'var(--danger)' }}
                  onClick={() => onDelete(editing)}
                >
                  Delete permanently
                </button>
              </>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setShowModal(false); setEditing(null); }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <PasswordPromptModal
        open={!!pwdTarget}
        title={pwdTarget ? `Confirm deletion of "${pwdTarget.name}"` : ''}
        body={
          <>
            Enter your account password to confirm. <strong>This cannot be undone.</strong>
          </>
        }
        confirmLabel="Delete site"
        error={pwdError}
        busy={pwdBusy}
        onConfirm={onPasswordConfirm}
        onCancel={() => {
          setPwdTarget(null);
          setPwdError(null);
          setPwdBusy(false);
        }}
      />
    </AppShell>
  );
}
